import { useCallback } from 'react';

interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
  ts: number;
}

// Lightweight in-memory pub/sub for debug panels.
// Keeps last N events; subscribers get notified on every trackEvent call.
const MAX_LOG = 25;
const eventLog: AnalyticsEvent[] = [];
type Listener = (evt: AnalyticsEvent) => void;
const listeners = new Set<Listener>();

export function getAnalyticsLog(): AnalyticsEvent[] {
  return eventLog.slice();
}

export function subscribeAnalytics(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export const useAnalytics = () => {
  const trackEvent = useCallback((event: string, properties?: Record<string, any>) => {
    // Track with Google Analytics 4 if available
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event, {
        custom_properties: properties,
        ...properties
      });
    }

    // Track with Plausible if available
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible(event, { props: properties });
    }

    // Push into in-memory log + notify listeners (used by debug panel)
    const entry: AnalyticsEvent = { event, properties, ts: Date.now() };
    eventLog.push(entry);
    if (eventLog.length > MAX_LOG) eventLog.shift();
    listeners.forEach((fn) => {
      try { fn(entry); } catch { /* noop */ }
    });

    // Fallback: console log for development
    if (import.meta.env.DEV) {
      console.log('Analytics Event:', { event, properties });
    }
  }, []);

  const trackPageView = useCallback((path: string) => {
    trackEvent('page_view', { page: path });
  }, [trackEvent]);

  const trackCTAClick = useCallback((location: string) => {
    trackEvent('cta_click', { location });
  }, [trackEvent]);

  const trackFormSubmission = useCallback((formType: string, success: boolean) => {
    trackEvent('form_submission', {
      form_type: formType,
      success,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackSectionView = useCallback((section: string) => {
    trackEvent('section_view', { section });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackCTAClick,
    trackFormSubmission,
    trackSectionView
  };
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    plausible?: (event: string, options?: { props?: Record<string, any> }) => void;
  }
}
